import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

// Pages
import LoginPage from '@/pages/LoginPage';
import AuthCallback from '@/pages/AuthCallback';
import StudentDashboard from '@/pages/StudentDashboard';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminScanner from '@/pages/AdminScanner';
import AdminStudents from '@/pages/AdminStudents';
import AdminReports from '@/pages/AdminReports';
import AdminUsers from '@/pages/AdminUsers';
import AdminAnalytics from '@/pages/AdminAnalytics';
import AdminBranches from '@/pages/AdminBranches';
import AdminImport from '@/pages/AdminImport';
import ProtectedRoute from '@/components/ProtectedRoute';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment synchronously during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardRouter />
        </ProtectedRoute>
      } />
      <Route path="/admin/scanner" element={
        <ProtectedRoute requireAdmin>
          <AdminScanner />
        </ProtectedRoute>
      } />
      <Route path="/admin/students" element={
        <ProtectedRoute requireAdmin>
          <AdminStudents />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute requireAdmin>
          <AdminReports />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute requireAdmin>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute requireAdmin>
          <AdminAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/admin/branches" element={
        <ProtectedRoute requireAdmin>
          <AdminBranches />
        </ProtectedRoute>
      } />
      <Route path="/admin/import" element={
        <ProtectedRoute requireAdmin>
          <AdminImport />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

// Dashboard router based on role
function DashboardRouter() {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (error) {
        console.error('Failed to fetch user', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-white text-xl font-heading">CARGANDO...</div>
      </div>
    );
  }
  
  if (user?.role === 'admin') {
    return <AdminDashboard />;
  }
  
  return <StudentDashboard />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-center" />
        <PWAInstallPrompt />
      </BrowserRouter>
    </div>
  );
}

export default App;
